export interface MetricViewEntry {
  visible: boolean
  passing_threshold?: number
  /**
   * Position among the report's metrics; absent until the user reorders. Read
   * as unknown because saved configs are free-form JSON.
   */
  order?: unknown
}

export type MetricViews = Record<string, MetricViewEntry>

const isOptionalNumber = (value: unknown) =>
  value === undefined || typeof value === "number"

export function isMetricViewEntry(value: unknown): value is MetricViewEntry {
  return (
    !!value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    typeof (value as { visible?: unknown }).visible === "boolean" &&
    isOptionalNumber(
      (value as { passing_threshold?: unknown }).passing_threshold,
    )
  )
}

/**
 * Sorts metric ids by their saved `order`. Ids without one keep their incoming
 * relative position after the ordered ones, so a metric new to the report
 * lands at the end.
 */
export function orderMetricIds(
  metricIds: readonly string[],
  metricViews: MetricViews | null | undefined,
): string[] {
  if (!metricViews) {
    return [...metricIds]
  }
  // A non-numeric saved `order` is kept verbatim but ranks as unordered.
  const rank = (id: string) => {
    const order = metricViews[id]?.order
    return typeof order === "number" && Number.isFinite(order)
      ? order
      : Infinity
  }
  // Infinity - Infinity is NaN, so equal ranks compare as 0 explicitly.
  return [...metricIds].sort((a, b) =>
    rank(a) === rank(b) ? 0 : rank(a) - rank(b),
  )
}

/**
 * Records `orderedIds` as each metric's `order`, keeping every other field
 * and every entry for a metric not in the list.
 */
export function applyMetricOrder(
  metricViews: MetricViews,
  orderedIds: readonly string[],
): MetricViews {
  const next: MetricViews = { ...metricViews }
  orderedIds.forEach((id, order) => {
    next[id] = {
      ...metricViews[id],
      visible: metricViews[id]?.visible ?? true,
      order,
    }
  })
  return next
}

export function normalizeMetricConfig(
  value: unknown,
): MetricViews | null | undefined {
  if (value == null) {
    return value
  }
  if (typeof value !== "object" || Array.isArray(value)) {
    return undefined
  }

  const entries = Object.entries(value)
  if (!entries.every(([, entry]) => isMetricViewEntry(entry))) {
    return undefined
  }

  return Object.fromEntries(entries) as MetricViews
}
